// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __ } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-loop-filter-condition' )
export class AutomatorLogItemLoopCondition extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties

	// The condition ID (post ID)
	@property( { type: Number, attribute: 'condition-id' } ) accessor id;

	// The condition code (e.g. WP_USER_EMAIL_ENDS_WITH)
	@property( { type: String, attribute: 'condition-code' } ) accessor code;

	// The integration code (e.g. WP)
	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	// Whether the sidebar of this item is open
	@property( { type: Boolean, attribute: 'is-open' } ) accessor isOpen = false;

	// The title of the condition
	@property( { type: String, attribute: false } ) accessor titleHTML = '';

	// The fields. These must follow the same structure all other fields array in the logs are following
	@property( { type: Array, attribute: false } ) accessor fields = [];

	// eslint-disable-next-line require-jsdoc
	render() {
		return html`
			<div class="condition">
				${ this._templateIcon }
				${ this._templateSentence }
				${ this._templateOpenSidebarButton }
			</div>
		`;
	}

	/**
	 * Keeps the sidebar up to date
	 * Called before update() to compute values needed during the update.
	 * We'll use this method to keep the sidebar up to date when one the reactive properties of the action change
	 *
	 * @param {Map} changedProperties The properties that changed
	 */
	willUpdate( changedProperties ) {
		// Check if the property that changed was .isOpen
		// If that's the case, WE DON'T NEED TO DO THIS HERE AGAIN
		// When the property .isOpen changes, `.requestSidebar()` is invoked, which
		// is already sending the request to the main component to update the sidebar data
		if ( changedProperties.has( 'isOpen' ) ) {
			return;
		}

		// Check if it's open
		if ( ! this.isOpen ) {
			return;
		}

		// Request sidebar update
		this.updateSidebar();
	}

	/**
	 * Opens the sidebar to show more data about this item
	 */
	requestSidebar() {
		if ( this.isOpen ) {
			// Close it
			this.dispatchEvent(
				new CustomEvent( 'uap-log-request-sidebar', {
					bubbles: true,
					composed: true,
					detail: {
						action: 'close',
					}
				} )
			);

			return;
		}

		this.updateSidebar();
	}

	/**
	 * Updates the sidebar data
	 */
	updateSidebar() {
		this.dispatchEvent(
			new CustomEvent( 'uap-log-request-sidebar', {
				bubbles: true,
				composed: true,
				detail: {
					action: 'open',
					sourceID: `loop-condition-${ this.id }`,
					content: this.sidebarContent
				}
			} )
		);
	}

	/**
	 * Creates the object passed to the sidebar
	 *
	 * @return {Object} The object that will be passed to the sidebar
	 */
	get sidebarContent() {
		return {
			title: escapeHTML( __( 'Loop filter', 'uncanny-automator' ) ),
			summary: this.sidebarSummary,
			runs: this.sidebarRuns
		}
	}

	/**
	 * Creates the summary for the sidebar
	 *
	 * @return {Object} Summary for the sidebar
	 */
	get sidebarSummary() {
		// Summary items
		const items = [];

		// items.push( {
		// 	label: '...',
		// 	value: html``
		// } );

		return {
			title: html`
				<uap-log-integration-icon integration-id="${ this.integrationCode }" show-tooltip></uap-log-integration-icon>
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			`,
			items: items
		}
	}

	/**
	 * Creates the array for the sidebar to render the condition fields
	 * Conditions don't have "Runs", but the sidebar expects an array with instances, so we create a single run and
	 * send the fields as properties
	 *
	 * @return {Array} The array of runs for the sidebar
	 */
	get sidebarRuns() {
		return [
			{
				title: escapeHTML( __( 'Properties', 'uncanny-automator' ) ),
				properties: this.fieldsForSidebar
			}
		];
	}

	/**
	 * Converts fields into properties for the sidebar
	 *
	 * @return {Array} where each element is a field of the condition, prepared for the sidebar
	 */
	get fieldsForSidebar() {
		// Prepare fields for sidebar
		const fieldsSidebar = [];

		this.fields.forEach( field => {
			// Get the value of the field that'll show in the sidebar
			// Loop filters will have a `parsed` property, which will contain the value of the tokens
			// instead of the token IDs
			let fieldValue = isDefined( field.value.parsed ) ? field.value.parsed : field.value.raw;

			// Selects will also contain a `readable` property, which will contain the text of the option
			if ( field.type !== 'file' && ! isEmpty( field.value.readable ) ) {
				fieldValue = `${ fieldValue } (${ field.value.readable })`;
			}

			fieldsSidebar.push( {
				type:  field.type,
				label: field.label,
				value: fieldValue,
				attributes: ! isEmpty( field.attributes ) ? field.attributes : {}
			} );
		} );

		return fieldsSidebar;
	}

	/**
	 * Returns the template that renders the integration icon
	 *
	 * @return {TemplateResult} The template of the integration icon
	 */
	get _templateIcon() {
		return html`
			<div class="condition__icon">
				<uo-icon integration="${ this.integrationCode }" show-tooltip></uo-icon>
			</div>
		`;
	}

	/**
	 * Returns the template that renders the condition sentence
	 *
	 * @return {TemplateResult} The template of the condition sentence
	 */
	get _templateSentence() {
		return html`
			<div class="condition__sentence">
				<uap-log-sentence .sentence="${ this.titleHTML }"></uap-log-sentence>
			</div>
		`;
	}

	/**
	 * Returns the template of the button that requests the sidebar
	 *
	 * @return {TemplateResult} The template of the button that requests the sidebar
	 */
	get _templateOpenSidebarButton() {
		return html`
			<uo-tooltip class="condition__toggle-sidebar">
				${ ! this.isOpen ? escapeHTML( __( 'Open in sidebar', 'uncanny-automator' ) ) : escapeHTML( __( 'Close sidebar', 'uncanny-automator' ) ) }

				<uo-button 
					slot="target"
					color="transparent" 
					@click=${ this.requestSidebar }
					size="small"
				>
					<uo-icon id="${ this.isOpen ? 'angle-left' : 'angle-right' }"></uo-icon>
				</uo-button>
			</uo-tooltip>
			
		`;
	}
}